import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Module-level flag: true once the user has navigated at least once
 * within the app (SPA route change). When false, the page was opened
 * directly (deep link / shared link / SEO landing), so going "back"
 * would leave the site — we use the fallback route instead.
 */
let hasInAppHistory = false;

/** Mount once near the app root to track in-app navigations. */
export function NavHistoryTracker() {
  const [location] = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    hasInAppHistory = true;
  }, [location]);
  return null;
}

interface BackButtonProps {
  /** Route used when there is no in-app history (deep link). */
  fallback?: string;
  label?: string;
  className?: string;
  /** Icon-only compact variant (no text label). */
  iconOnly?: boolean;
}

export default function BackButton({
  fallback = "/home",
  label = "Back",
  className,
  iconOnly = false,
}: BackButtonProps) {
  const [, setLocation] = useLocation();

  const handleBack = () => {
    if (hasInAppHistory && window.history.length > 1) {
      window.history.back();
    } else {
      setLocation(fallback);
    }
  };

  return (
    <Button
      variant="ghost"
      size={iconOnly ? "icon" : "sm"}
      onClick={handleBack}
      aria-label={label}
      className={cn(
        "text-muted-foreground hover:text-foreground",
        !iconOnly && "gap-1.5 -ml-2",
        className,
      )}
      data-testid="button-back"
    >
      <ArrowLeft className={iconOnly ? "h-5 w-5" : "h-4 w-4"} />
      {!iconOnly && label}
    </Button>
  );
}
